import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { LoaderCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

export const buttonVariants = cva(
  cn(
    'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-md',
    'font-medium transition-colors duration-fast ease-standard',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ),
  {
    variants: {
      variant: {
        primary: 'bg-primary text-primary-fg shadow-xs hover:bg-primary-hover active:bg-primary-active',
        secondary: 'border border-border-strong bg-surface text-fg shadow-xs hover:bg-surface-hover',
        // Disparador de un CAMPO de formulario (combobox, selector de fecha).
        // Igual que `secondary` salvo el contorno: al ser un control de entrada
        // le aplica el 3:1 de WCAG 1.4.11, que `border-border-strong` no
        // alcanza. Es el mismo borde que usan Input, Select y Checkbox.
        field: 'border border-border-input bg-surface text-fg shadow-xs hover:bg-surface-hover',
        outline: 'border border-border-strong bg-transparent text-fg hover:bg-surface-hover',
        ghost: 'bg-transparent text-fg-muted hover:bg-surface-hover hover:text-fg',
        subtle: 'bg-primary-subtle text-primary-subtle-fg hover:bg-primary-subtle/70',
        danger: 'bg-danger text-danger-fg shadow-xs hover:bg-danger-hover',
        link: 'bg-transparent text-link underline-offset-4 hover:text-link-hover hover:underline',
      },
      size: {
        xs: 'h-control-xs px-2 text-2xs [&_svg]:size-3.5',
        sm: 'h-control-sm px-3 text-xs [&_svg]:size-4',
        md: 'h-control-md px-4 text-sm [&_svg]:size-4',
        lg: 'h-control-lg px-6 text-base [&_svg]:size-5',
        'icon-sm': 'h-control-sm w-8 [&_svg]:size-4',
        icon: 'h-control-md w-10 [&_svg]:size-4',
        'icon-lg': 'h-control-lg w-12 [&_svg]:size-5',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Renderiza en el hijo (p. ej. un `<Link>`) en vez de en un `<button>`. */
  asChild?: boolean;
  /** Muestra el girador y bloquea el botón sin cambiar su ancho. */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, loading = false, disabled, children, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      // `disabled` no existe en un `<a>`: con `asChild` se omite para no
      //  ensuciar el DOM con un atributo que el navegador ignora.
      disabled={asChild ? undefined : (disabled ?? loading)}
      // Un lector de pantalla debe saber que el botón está trabajando; sin
      // esto, «Guardando…» sólo existe para quien ve el girador.
      aria-busy={loading || undefined}
      {...props}
    >
      {/* Con `asChild`, Radix hace `React.Children.only`: hay que pasarle el
          hijo A SECAS. Un `{null}` delante ya cuenta como segundo hijo y el
          Slot revienta, así que el girador se omite en ese modo (y tampoco
          haría falta: ahí el botón es un enlace y no hay nada que esperar). */}
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
          {children}
        </>
      )}
    </Comp>
  );
});
